import { MailState } from 'App/babel/components/MailDrawers/MailContext';
import Mail, { MailOpportunity, MailRecipient, MailRecipients } from 'App/resources/Model/Mail';
import Contact, { ContactMail } from 'App/resources/Model/Contact';
import T from 'Components/Helpers/translate';
import { IntegrationConfig } from 'App/resources/AllIWant';
import logError from 'Helpers/logError';
import { openDrawer } from 'Services/Drawer';
import StandardIntegrationData from 'Resources/StandardIntegrationData';
import RequestBuilder, { comparisonTypes } from 'Resources/RequestBuilder';
import { hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import openModal from 'App/services/Modal';
import { FieldModel } from 'App/components/FormObserver';
import ContactResource from 'Resources/Contact';

//check if mailIntegration config is valid
export const mailIntegrationIsValid = async (mailIntegration: IntegrationConfig, onReject?: () => void) => {
	const data = {
		type: 'validateMailIntegration',
		typeId: '',
		data: {},
		integrationId: mailIntegration.id
	};
	try {
		const { data: validateResult } = await StandardIntegrationData.run<{ valid?: boolean }>(data);
		return !!validateResult?.valid;
	} catch (err: any) {
		//109 and 121 are standardintegration errors
		if ([109, 121].includes(err.response?.data?.error?.errorCode)) {
			// if StandardIntegrationvallidate request fails, something is wrong with the setup.
			return false;
		} else {
			logError(err, 'Could not get a valid mail integration');
			return onReject?.();
		}
	}
};

export const MAIL_COMPANY_DISAMBIGUATION = 'MAIL_COMPANY_DISAMBIGUATION';

/** The address on the other end of the mail — the one that identifies the company. */
export const getCounterpartEmail = (mail: Pick<Mail, 'type' | 'to' | 'from'>) =>
	(mail.type === 'out' ? mail.to : mail.from)?.trim().toLowerCase() ?? '';

/**
 * A synced mail with no company is one the mail import refused to guess a company
 * for, because the counterpart email is a contact on several companies. The
 * externalMailId clause keeps out mail that never came from a mail integration.
 * Kept next to addNeedsCompanyFilters so the row check and the list query agree.
 */
export const needsCompanySelection = (mail: Pick<Mail, 'client' | 'externalMailId'>) =>
	hasSoftDeployAccess(MAIL_COMPANY_DISAMBIGUATION) && !mail.client?.id && !!mail.externalMailId;

export const addNeedsCompanyFilters = (rb: RequestBuilder, needsCompany: boolean = true) => {
	rb.addFilter({ field: 'externalMailId' }, comparisonTypes.NotEquals, null);
	rb.addFilter({ field: 'client.id' }, needsCompany ? comparisonTypes.Equals : comparisonTypes.NotEquals, null);
};

const doesRecipientsExceedMaxSize = (recipients: MailRecipient[]) => {
	const MAX_SIZE = 150;
	let len = -1;
	// Because they are saved with a semicolon between each other we add +1 to all except the first one, hence len = -1;
	recipients.forEach(rec => {
		len += rec.email.length + 1;
	});
	return len > MAX_SIZE;
};

export const getTooltipIfRecipientsExceedMaxSize = (recipients: MailRecipients) => {
	if (doesRecipientsExceedMaxSize(recipients.cc)) {
		return T('mailDrawer.tooManyRecipientsCc');
	}
	if (doesRecipientsExceedMaxSize(recipients.bcc)) {
		return T('mailDrawer.tooManyRecipientsBcc');
	}
	return undefined;
};

type UserMail = { id: number; name: string; role?: unknown; email: string };

function splitRecipients(recipients: MailRecipient[]): MailRecipient[] {
	return recipients.flatMap(recipient => {
		if (recipient.email && recipient.email.includes(',')) {
			return recipient.email.split(',').map(email => ({
				...recipient,
				email: email.trim(),
				type: recipient.type as 'user' | 'contact' | 'other'
			}));
		}
		return recipient;
	});
}

export const formatRecipients = (mailData: Mail) => {
	if (!mailData.recipients) {
		const cc = (mailData.cc ?? []).map(email => ({ email, type: 'other' as const })) as MailRecipient[];
		const bcc = (mailData.bcc ?? []).map(email => ({ email, type: 'other' as const })) as MailRecipient[];
		const contact: ContactMail | undefined = mailData.contact;
		const user: UserMail | undefined = mailData.users?.length ? mailData.users[0] : undefined;
		let recipient: MailRecipient;
		if (mailData.type === 'in' && user) {
			recipient = { id: user?.id, type: 'user', email: user.email, name: user?.name };
		} else if (mailData.type === 'out' && contact) {
			recipient = { id: contact?.id, type: 'contact', email: contact.email, name: contact.name };
		} else {
			recipient = { type: 'other', email: mailData.to };
		}
		mailData.recipients = {
			to: [recipient],
			cc: cc,
			bcc: bcc
		};
		return mailData;
	}

	mailData.recipients.to = splitRecipients(mailData.recipients.to || []);
	mailData.recipients.cc = splitRecipients(mailData.recipients.cc || []);
	mailData.recipients.bcc = splitRecipients(mailData.recipients.bcc || []);

	// map each recipient to user/contact if possible
	const mapRecipient = (recipient: MailRecipient): MailRecipient => {
		// try to find a matching user by email
		const user = (mailData.users || []).find(u => u.email === recipient.email);
		if (user) {
			return { ...recipient, id: user.id, name: user.name, type: 'user' as const };
		}
		// try to find a matching contact by email
		const contact = mailData.contact && mailData.contact.email === recipient.email ? mailData.contact : undefined;
		if (contact) {
			return { ...recipient, id: contact.id, name: contact.name, type: 'contact' as const };
		}
		// fallback: set name to email
		return { ...recipient, name: recipient.email, type: recipient.type as 'user' | 'contact' | 'other' };
	};

	mailData.recipients.to = mailData.recipients.to.map(mapRecipient);
	mailData.recipients.cc = mailData.recipients.cc.map(mapRecipient);
	mailData.recipients.bcc = mailData.recipients.bcc.map(mapRecipient);

	return mailData;
};

export const toMailOpportunity = (order: MailOpportunity & { value?: number }): MailOpportunity => ({
	id: order.id,
	date: order.date,
	// The order modal keeps the amount in `value`; `orderValue` is the name the api mapping
	// gives it (mapping/order.js: orderValue -> value), so only api-shaped orders have it.
	orderValue: order.orderValue ?? order.value,
	probability: order.probability,
	description: order.description,
	currency: order.currency
});

export type FormSubmitReference = { formId: number; contactId: number; activityRegDate?: Date | string };

type ActivitySource = { source?: { type?: string; id?: number } | null; regDate?: Date | string };

// Form actions stamp the form's id, not the submit's, so the panel has to find the submit itself.
export const getFormSubmitReference = (
	activity?: ActivitySource | null,
	contactId?: number
): FormSubmitReference | undefined => {
	const source = activity?.source;
	if (source?.type !== 'form' || !source.id || !contactId) {
		return undefined;
	}
	return { formId: source.id, contactId, activityRegDate: activity?.regDate };
};

type NewMailExtraProps = Partial<Mail> & { formSubmitReference?: FormSubmitReference };

export const openNewMailWithContact = (contact?: Partial<Contact> | null, extraMailProps?: NewMailExtraProps) => {
	if (!contact?.email) {
		openDrawer('NewSingleMail', { ...extraMailProps });
		return;
	}
	const { id, name, email, client, title } = contact;
	const to: MailRecipient = { id, name, email, client, type: 'contact', title };
	openDrawer('NewSingleMail', {
		recipients: { to: [to], cc: [], bcc: [] },
		client,
		...extraMailProps
	});
};

/**
 * Mail a contact, asking for an address first if it has none.
 *
 * Angular's `ContactCtrl.emailContact` (`contact.js`) prompted for the address,
 * saved it onto the contact and only then opened the composer. Without that,
 * `openNewMailWithContact` opens an empty drawer: the rep types the address by
 * hand, it is never persisted, and the next mail asks again.
 *
 * The prompt is the React `AskForInput` modal rather than Angular's `infoPrompt`
 * — the whole point of the port is to stop reaching back into a tree that is
 * being deleted. `FieldModel.email().required()` is Angular's
 * `required: true, type: 'email'`; an invalid address saved onto the contact
 * would be worse than the empty drawer this replaces.
 */
export const openNewMailWithContactOrAskForEmail = (contact: Contact, extraMailProps?: Partial<Mail>) => {
	if (contact.email) {
		openNewMailWithContact(contact, extraMailProps);
		return;
	}

	openModal('AskForInput', {
		title: 'default.enterEmail',
		formModel: { email: FieldModel.email('default.email').required() },
		initialValues: { email: '' },
		// `AskForInput` hands back the whole form, typed only as a string-keyed bag
		// — its generic is not inferred from `initialValues` at the openModal call.
		onClose: (values?: Record<string, unknown>) => {
			const email = typeof values?.email === 'string' ? values.email.trim() : '';
			// Cancelling opens nothing, matching Angular's `if (email)` guard.
			if (!email) {
				return;
			}
			ContactResource.save({ id: contact.id, email })
				// Passed explicitly rather than waiting for the `contact.updated`
				// the save broadcasts: that round-trips through the card's state,
				// which is not guaranteed to have landed by the time the drawer
				// reads its recipient.
				.then(() => openNewMailWithContact({ ...contact, email }, extraMailProps))
				.catch(err => logError(err, 'Failed to save the contact email'));
		}
	});
};

export const openNewMailDrawerWithCopiedData = (mail: Mail) => {
	// Wait so that the previous drawer is closed first
	setTimeout(() => {
		openDrawer('NewSingleMail', {
			subject: mail.subject,
			body: mail.body,
			attachments: mail.attachments
		});
	}, 400);
};

export const previewInBrowser = async (state: MailState) => {
	const {
		from,
		subject,
		body,
		recipients: { to, cc, bcc }
	} = state;
	const preview = window.open(
		'',
		'',
		'height=768,width=1024,scrollbars=yes,resizable=yes,toolbar=no,status=no,menu=no,titlebar=no,location=no,addressbar=no'
	) as Window;

	const recipientRow = (title: string, recipient: MailRecipient[]) => `<tr>
			<td>
				<b>${title}:</b>
			</td>
			<td>${recipient.map(r => ' ' + (r.name ?? r.email))}</td>
	</tr>`;

	const html = `<head>
		<style>
			body {
				margin: 0;
				border-top: 5px solid #002f65;
				color: #333;
				font-family: Helvetica;
				font-size: 14px;
			}
			#info h2 {
				color: #002f65;
				font-family: Helvetica;
				font-size: 22px;
				margin-bottom: 6px;
			}
			#info p{
				margin-top: 2px;
				margin-bottom: 2px;
			}
			#emailHeaders {
				font-size:12px;
			}
			#emailHeaders tr {
				border-bottom: 1px solid #f0f0f0;
			}
		</style>
	</head>
	<body>
		<table border=0 width="100%" cellpadding="0" cellspacing="0">
			<tr width="100%">
				<td width="100%" id="info" bgcolor="#fff">
					<font>
						<h2>
							${T('mail.preview')} ${T('mail.mail').toLowerCase()}
						</h2>
						<p>${T('mail.previewDisclaimer')}.</p>
					</font>
				</td>
			</tr>
			<tr width="100%">
				<td width="100%" id="emailHeaders">
					<br/>
					<table border=0 width="100%">
						<tr width="100%">
							<td>
								<b>${T('mail.from')}:</b>
							</td>
							<td>
								${from?.name}
							</td>
						</tr>
						${recipientRow(T('mail.to'), to)}
						${cc.length ? recipientRow(T('mail.cc'), cc) : ''}
						${bcc.length ? recipientRow(T('mail.bcc'), bcc) : ''}
						<td>
							<b>${T('mail.subject')}:</b>
						</td>
							<td>${subject}</td>
						</tr>
					</table>
					<br/>
				</td>
			</tr>
			<tr>
				<td id="previewBody">
					${body || ''}
				</td>
			</tr>
		</table>
	</body>`;

	var toggleStyle =
		' style="width: 50%; height: 40px; line-height: 40px; background-color: #fff; border: 1px solid #ccc; color: #888; cursor: pointer"';
	var toggles = `<div style="position: absolute; width: 100%; z-index: 10000; top: 0; left: 0; font-family: sans-serif;"><button type="button"
		${toggleStyle}
		onClick=document.getElementById("preview-iframe").style.width="100%"><b class="fa fa-desktop"></b>
		${T('ads.desktop')}
		</button>`;
	toggles += `<button type="button"'
		${toggleStyle}
		onClick=document.getElementById("preview-iframe").style.width="400px"><b class="fa fa-mobile" style="margin-right: 3px;"></b>
		${T('default.phone')}
		</button></div>`;
	preview.document.write(
		toggles +
			'<div style="width: 100%; height: 100%; display: flex; justify-content: center; position: absolute; left: 0;"><iframe id="preview-iframe" style="width: 100%; height: 100%;"></iframe></div>'
	);
	const iframe = preview.document.getElementById('preview-iframe') as any;
	const script = preview.document.createElement('script');
	script.src = 'https://use.fontawesome.com/01a8d84c9a.js';
	preview.document.head.appendChild(script);
	iframe.contentWindow.document.open();
	iframe.contentWindow.document.write(html);
	iframe.contentWindow.document.close();
};
